/**
 * usePageTracking
 * Automatically tracks page views on route changes
 * Works for both authenticated and anonymous pages
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { eventService } from '@/services/tracking/eventService';

export function usePageTracking(): void {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);
  const pageEnteredAt = useRef<number>(Date.now());

  useEffect(() => {
    // Strip sensitive tokens from query strings before tracking
    let search = location.search || '';
    if (search) {
      const params = new URLSearchParams(search);
      params.delete('token');
      const cleaned = params.toString();
      search = cleaned ? `?${cleaned}` : '';
    }
    const currentPath = location.pathname + search;

    // Calculate time spent on previous page
    const now = Date.now();
    const durationMs = previousPath.current ? now - pageEnteredAt.current : undefined;

    // Use document.referrer for the first page (external referrer), then previous path
    const referrer = previousPath.current ?? (typeof document !== 'undefined' ? document.referrer : undefined) ?? undefined;

    // Track the page view
    eventService.trackPageView(
      currentPath,
      referrer,
      durationMs
    );

    // Update refs for next navigation
    previousPath.current = currentPath;
    pageEnteredAt.current = now;
  }, [location.pathname, location.search]);
}
